import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useSnapshot } from 'valtio';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { StudentScheduleItem } from '@duke-hq/svc-api/student/services/schedulerService';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { useMemo } from 'react';
import { internalEnrolmentState } from '@duke-hq/app-provider-web/tenant/tenantState/InternalEnrolmentState';
import { ConsultantBanner } from '@duke-hq/app-provider-web/tenant/components/InternalEnrolmentForms/ConsultantBanner';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';

type ReviewCourseProps = {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
};

export function ReviewCourse({ goToNextStep, goToPrevStep, setStep }: ReviewCourseProps) {
  const modalState = useSnapshot(internalEnrolmentState);

  const { data: courseData, isLoading } = trpcTenant.courses.byId.useQuery(
    modalState.selectedCourseAttributes.id,
    {
      enabled: !!modalState.selectedCourseAttributes.id,
    },
  );

  if (!modalState.selectedCourseAttributes.id) {
    return (
      <FormMainContainer>
        <FormContentContainer>
          <Box w="full" textAlign="left">
            <Text color="fg.muted" textStyle="sm">
              <Trans>No course is selected. Please select one to continue.</Trans>
            </Text>
          </Box>
        </FormContentContainer>
        <FormFooterContainer>
          <div />
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    );
  }

  return (
    <FormMainContainer>
      <FormContentContainer>
        <ConsultantBanner
          firstName={modalState.selectedUserAttributes.first_name}
          lastName={modalState.selectedUserAttributes.last_name}
          email={modalState.selectedUserAttributes.email}
          role={modalState.selectedUserAttributes.role}
          canChangeConsultant={false}
          onChangeClick={() => setStep(0)}
        />
        {isLoading ? (
          <>
            <VStack w="full" justify="start" align="start" gap="6px">
              {modalState.selectedCourseAttributes.identifier ? (
                <Text fontSize="md" lineHeight="6" color="fg.muted">
                  {modalState.selectedCourseAttributes.identifier}
                </Text>
              ) : null}
              <Box overflow="hidden" alignSelf="stretch">
                <Text fontSize="2xl" lineHeight="8" color="fg.default" fontWeight="bold">
                  {modalState.selectedCourseAttributes.title}
                </Text>
              </Box>
            </VStack>
            <Center h="full" w="full">
              <Spinner color="border.accent" size="lg" />
            </Center>
          </>
        ) : (
          <CourseReview
            data={courseData}
            title={modalState.selectedCourseAttributes.title}
            identifier={modalState.selectedCourseAttributes.identifier}
          />
        )}
      </FormContentContainer>
      <FormFooterContainer>
        <Button rightIcon={<ChevronRightIcon />} onClick={goToNextStep}>
          <Trans>Next</Trans>
        </Button>
        <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}

interface CourseReviewProps {
  data: RouterOutputs['courses']['byId'] | undefined;
  title: string;
  identifier: string;
}

function CourseUnitOfCompetency({ unit }: { unit: StudentScheduleItem }) {
  return (
    <Flex
      align="start"
      justify="center"
      direction="column"
      border="1px solid"
      borderColor="border.default"
      borderRadius="8px"
      padding="16px"
      bg="bg.subtle"
      w="full"
    >
      {unit?.['Unit Code'] ? (
        <Text color="fg.muted" fontSize="sm">
          {unit['Unit Code']}
        </Text>
      ) : null}
      <VStack align="start" justify="start" spacing="4px">
        <Text color="fg.emphasis" textStyle="md" fontWeight="semibold">
          {unit.Title}
        </Text>
      </VStack>
    </Flex>
  );
}

export function CourseReview({ data, title, identifier }: CourseReviewProps) {
  const courseReferralModalSnap = useSnapshot(internalEnrolmentState);
  const { hasWorkPlacement, schedulerData } = useMemo(() => {
    const hasWorkPlacement = data?.courseItems.some(
      (item) => item.unitOfCompetency?.work_placement === true,
    );
    const schedulerData =
      Array.isArray(data?.schedule) && data?.schedule?.length
        ? data.schedule.filter((item: StudentScheduleItem) => item?.type === 'UNIT')
        : [];

    return { hasWorkPlacement, schedulerData };
  }, [data]);

  if (!data) {
    return (
      <VStack w="full" h="full" justify="start" align="start" gap="10px">
        <VStack w="full" justify="start" align="start" gap="6px">
          {identifier ? (
            <Text textStyle="md" color="fg.muted">
              {identifier}
            </Text>
          ) : null}
          <Box overflow="hidden" alignSelf="stretch">
            <Text textStyle="2xl" color="fg.default" fontWeight="bold">
              {title}
            </Text>
          </Box>
        </VStack>
        <Text color="fg.muted" textStyle="sm">
          <Trans>Sorry, we couldn't load the course data. Please refresh or try again later.</Trans>
        </Text>
      </VStack>
    );
  }

  return (
    <VStack w="full" h="full" justify="start" align="start" gap="10px">
      <VStack w="full" justify="start" align="start" gap="6px">
        {courseReferralModalSnap.selectedCourseAttributes.identifier ? (
          <Text fontSize="md" color="fg.muted">
            {courseReferralModalSnap.selectedCourseAttributes.identifier}
          </Text>
        ) : null}
        <Box overflow="hidden" alignSelf="stretch">
          <Text textStyle="2xl" color="fg.default" fontWeight="bold">
            {courseReferralModalSnap.selectedCourseAttributes.kind ===
            CourseKindSchema.enum.SKILL_SET
              ? `Skill set: ${courseReferralModalSnap.selectedCourseAttributes.title}`
              : courseReferralModalSnap.selectedCourseAttributes.title}
          </Text>
        </Box>
        <HStack gap="8px" justify="start" align="center">
          <HStack gap="4px" justify="start" align="center">
            <Text fontSize="sm" lineHeight="5" color="fg.muted">
              <Trans>Cost</Trans>:
            </Text>
            <Text fontSize="sm" lineHeight="5" color="fg.default" fontWeight="medium">
              ${courseReferralModalSnap.selectedCourseAttributes.price_aud.toString()}
            </Text>
          </HStack>
          {hasWorkPlacement === true ? (
            <>
              <Text fontSize="sm" lineHeight="5" color="fg.subtle">
                •
              </Text>
              <Text fontSize="sm" lineHeight="5" color="warning.deep" fontWeight="medium">
                <Trans>Requires work placement</Trans>
              </Text>
            </>
          ) : null}
        </HStack>
        <Box overflow="hidden" alignSelf="stretch">
          <Text fontSize="sm" color="fg.muted">
            {courseReferralModalSnap.selectedCourseAttributes.specialisation}
          </Text>
        </Box>
      </VStack>

      <Box w="full" h="full">
        <UnorderedList
          listStyleType="none"
          overflowY="auto"
          minHeight="100px"
          maxHeight="400px"
          p={0}
          m={0}
          w="full"
        >
          {data.description != null ? (
            <Text textStyle="sm" color="fg.muted" mb={4}>
              {data.description}
            </Text>
          ) : null}

          {schedulerData.length
            ? schedulerData.map((item: StudentScheduleItem, index: number) => (
                <ListItem key={item.id} w="full">
                  <VStack w="full" align="center" gap="0">
                    <CourseUnitOfCompetency unit={item} />
                    {index !== schedulerData.length - 1 && (
                      <Box bg="border.emphasis" w="2px" h="16px" />
                    )}
                  </VStack>
                </ListItem>
              ))
            : null}
        </UnorderedList>
      </Box>
    </VStack>
  );
}
